/**
 * Core type definitions for the IWT Adjacency Map.
 *
 * The AdjacencyNode interface is the single source of truth for the data model.
 * It mirrors the SharePoint Product Priority Tracker columns while adding
 * adjacency-specific fields from the Bain "Profit from the Core" framework.
 */

// ── Enums ────────────────────────────────────────────────────────────

/** Strategic initiative groupings. Mirrors SP "Strategic Initiative" column. */
export type StrategicInitiative =
  | 'US Mining'
  | 'Proximity Detection'
  | 'Analytics'
  | 'Maritime'
  | 'International Mining'
  | 'New Verticals'
  | 'Capabilities'
  | 'Other';

/** Product lifecycle phase. Mirrors SP "Phase" column.
 * Known values listed explicitly for autocomplete; `(string & {})` accepts
 * any new value SP may introduce in the future. */
export type Phase =
  | 'Discovery'
  | 'Definition'
  | 'Feasibility'
  | 'Development'
  | 'Beta'
  | 'Launch'
  | 'Production'
  | (string & {});

/** Execution status. Mirrors SP "Status" column.
 * Known values listed explicitly for autocomplete; `(string & {})` accepts
 * any new value SP may introduce in the future. */
export type Status =
  | 'In Progress'
  | 'On Hold'
  | 'Not Started'
  | 'Proposed/Pending'
  | 'Complete'
  | 'Blocked'
  | 'Completed'
  | (string & {});

/** Adjacency axis — one of eight strategic directions plus Core. */
export type AdjacencyAxis =
  | 'New Services'
  | 'New Markets & Customers'
  | 'Forward Integration'
  | 'New Technologies & Capabilities'
  | 'New Channels'
  | 'New Geographies'
  | 'Backward Integration'
  | 'New Products'
  | 'Core';

/**
 * Relatedness ring — distance from core business.
 * 0 = Core, 1 = 1-Step (~60% success), 2 = 2-Step (~35%),
 * 3 = 3-Step (~15%), 4 = Diversification (<5%).
 */
export type RelatednessRing = 0 | 1 | 2 | 3 | 4;

/** Rich edge connecting two nodes. */
export interface ConnectionEdge {
  targetId: string;
  relationship: 'parent' | 'child' | 'peer';
  lineStyle: 'solid' | 'dashed' | 'dotted';
  lineColor: string;
  label?: string;
  /** Server edge row id (WQ-340). Absent in pre-WQ-340 snapshots and local-only data. */
  edgeId?: string;
}

/** Node classification — derived from legacy PDF map color coding. */
export type NodeType = 'product' | 'capability' | 'market' | 'candidate';

/**
 * Data origin — controls sync behavior and visual styling.
 * - priority-list: Actively tracked in SP Priority List, synced when enabled
 * - adjacency-map: Imported from PDF adjacency map, may be historical/aspirational
 * - manual: Added through the application UI
 */
export type DataSource = 'priority-list' | 'adjacency-map' | 'manual';

/** Available view modes in the application. */
export type ViewMode = 'map' | 'list' | 'matrix' | 'dashboard' | 'finance' | 'resources' | 'timeline';

// ── Core Data Model ──────────────────────────────────────────────────

/** Manual position override for map placement. */
export interface MapPosition {
  x: number;
  y: number;
}

/**
 * AdjacencyNode — the central data type for every item in the system.
 *
 * 22 fields total. Mirrors SharePoint Product Priority Tracker columns
 * while adding adjacency-specific fields for the Bain framework.
 */
export interface AdjacencyNode {
  /** Unique identifier (UUID v4). Maps to SP "ID". */
  id: string;

  /** Product or capability name. Maps to SP "Title". */
  name: string;

  /** Full description. Maps to SP "Description". */
  description: string;

  /** Strategic initiative grouping. Maps to SP "Strategic Initiative". */
  strategicInitiative: StrategicInitiative;

  /** Product lifecycle phase. Maps to SP "Phase". */
  phase: Phase | null;

  /** Execution status. Maps to SP "Status". */
  status: Status | null;

  /** Priority number within initiative. Maps to SP "Priority Number". */
  priority: number | null;

  /** Position on one of 8 strategic axes (+ Core). */
  adjacencyAxis: AdjacencyAxis;

  /** Distance from core: 0=Core through 4=Diversification. */
  relatednessRing: RelatednessRing;

  /** Bain dimension: shared customers overlap (1–10). */
  sharedCustomers: number;

  /** Bain dimension: shared cost structure overlap (1–10). */
  sharedCosts: number;

  /** Bain dimension: common competitors overlap (1–10). */
  commonCompetitors: number;

  /** Composite attractiveness score (1–10) per Bain funnel. */
  attractivenessScore: number;

  /** Whether this adjacency reinforces the core business. */
  reinforcesCore: boolean;

  /** Classification derived from legacy PDF map colors. */
  nodeType: NodeType;

  /** Data origin — controls sync behavior and visual styling. */
  source: DataSource;

  /** Rich edges to connected/dependent nodes. */
  edges: ConnectionEdge[];

  /** IDs of parent/category nodes. Empty array if top-level or no hierarchy. */
  parentIds: string[];

  /** Optional manual position override for map placement. */
  mapPosition: MapPosition | null;

  /** SP "Priority" column — text/choice field used by the Priority List view filter.
   *  Values: 'Priority' | 'Proposed / Pending' | '' (empty).
   *  NOT the same as `priority` (which is the numeric Priority Number).
   *  Read-only from SP — never pushed. */
  spPriorityTag: string | null;

  /** SP sync review status — routes new/removed SP items to review queue.
   *  null = normal (visible on map), 'pending-addition' = new from SP awaiting review,
   *  'pending-removal' = removed from SP awaiting review. (Task 6.10) */
  spSyncReviewStatus: 'pending-addition' | 'pending-removal' | null;

  /** SharePoint list item ID for bidirectional sync. Null if not SP-sourced. */
  spListItemId: number | null;

  /** ISO 8601 timestamp of last successful sync. Null if never synced. */
  lastSyncedAt: string | null;

  /** ISO 8601 creation timestamp. */
  createdAt: string;

  /** ISO 8601 last modification timestamp. */
  modifiedAt: string;

  /** Snapshot of SP-native field values captured at last successful sync.
   *  Used to compute diff for the Pending SP Changes modal.
   *  null if node has never been synced or predates this feature. */
  spSyncSnapshot: Record<string, unknown> | null;

  // ── Financial Fields ──────────────────────────────────────────────

  /** Estimated R&D cost. Syncs bidirectionally with SP "Est R&D" column. */
  estRnD: number | null;

  /** Estimated revenue. Syncs bidirectionally with SP "Est Rev" column. */
  estRevenue: number | null;

  /** Internal R&D investment — IWT-funded dollars from budget planning.
   *  App-only enrichment, never pushed to SP. */
  iRnD: number | null;

  /** Funded R&D — external money (government, customer) that offsets investment cost.
   *  App-only enrichment, never pushed to SP. */
  fRnD: number | null;

  /** Total budget estimate (e.g. "Matt's Estimate" from planning spreadsheet).
   *  App-only enrichment. */
  totalBudgetEstimate: number | null;

  // ── Market Sizing (Future AI-Assisted) ───────────────────────────

  /** Total Addressable Market in dollars. App-only.
   *  Marked for future AI consideration as a starting point when requested by user. */
  tam: number | null;

  /** Serviceable Addressable Market in dollars. App-only.
   *  Marked for future AI consideration as a starting point when requested by user. */
  sam: number | null;

  /** Whether TAM value was AI-suggested (true) or user-entered (false/null).
   *  When true, UI should show an indicator that this is an AI estimate. */
  tamAiSuggested: boolean | null;

  /** Whether SAM value was AI-suggested (true) or user-entered (false/null). */
  samAiSuggested: boolean | null;

  // ── Timeline Fields ─────────────────────────────────────────────────

  /** Project start date (ISO 8601). App-only — drives Gantt bar start. */
  startDate: string | null;

  /** Project end date (ISO 8601). App-only — drives Gantt bar end. */
  endDate: string | null;

  /** Market Requirements Date (ISO 8601). Syncs bidirectionally with SP. */
  mrdDate: string | null;

  /** MRD Target date (ISO 8601). Syncs bidirectionally with SP. */
  mrdTargetDate: string | null;

  /** Authorized to Develop date (ISO 8601). Syncs bidirectionally with SP. */
  atdDate: string | null;

  /** Authorized to Quote date (ISO 8601). Syncs bidirectionally with SP. */
  atqDate: string | null;

  /** Authorized to Sell date (ISO 8601). Syncs bidirectionally with SP. */
  atsDate: string | null;

  /** Optional Gantt chart start date override (ISO 8601). App-only.
   *  Used when SP dates are incomplete or for manual timeline planning. */
  ganttStartDate: string | null;

  /** Optional Gantt chart end date override (ISO 8601). App-only. */
  ganttEndDate: string | null;

  // ── Resource Fields ──────────────────────────────────────────────

  /** FTE allocation for this project. App-only enrichment. */
  fteAllocation: number | null;

  /** Whether this project uses IWT South resources. App-only. */
  iwtSouth: boolean;

  /** IWT South FTE count for this project. App-only. */
  iwtSouthFte: number | null;

  /** Whether this is a Minimum Viable Product. App-only. */
  isMvp: boolean;

  // ── SP Bidirectional Informational Fields ────────────────────────

  /** Development lane/track. Syncs bidirectionally with SP. */
  devLane: string | null;

  /** Development team. Syncs bidirectionally with SP. */
  devTeam: string | null;

  /** Product manager. Syncs bidirectionally with SP. */
  productManager: string | null;

  /** Product line grouping. Syncs bidirectionally with SP. */
  productLine: string | null;

  // ── Budget Linking ───────────────────────────────────────────────

  /** Reference name linking to budget spreadsheet project for cross-referencing.
   *  App-only. Null if not linked to a budget project. */
  budgetProjectName: string | null;
}

// ── Derived / Utility Types ──────────────────────────────────────────

/** Fields required when creating a new node (auto-generated fields excluded). */
export type CreateNodeInput = Omit<AdjacencyNode, 'id' | 'createdAt' | 'modifiedAt'>;

/** Fields allowed when updating a node. */
export type UpdateNodeInput = Partial<Omit<AdjacencyNode, 'id' | 'createdAt'>>;

// ── Filter Types ─────────────────────────────────────────────────────

/** Relationship types that can be filtered. */
export type RelationshipType = 'parent-child' | 'related';

/** Filter state for narrowing the displayed node set. */
export interface NodeFilters {
  initiatives: StrategicInitiative[];
  statuses: Status[];
  phases: Phase[];
  axes: AdjacencyAxis[];
  ringRange: [RelatednessRing, RelatednessRing];
  sources: DataSource[];
  searchText: string;
  /** When non-empty, only show connection lines for these relationship types. Empty = show all. */
  relationshipFilter: RelationshipType[];
}

/** Default filter state — everything visible. */
export const DEFAULT_FILTERS: NodeFilters = {
  initiatives: [],
  statuses: [],
  phases: [],
  axes: [],
  ringRange: [0, 4],
  sources: [],
  searchText: '',
  relationshipFilter: [],
};

// ── Sync Types ───────────────────────────────────────────────────────

/** Result of a sync operation. */
export interface SyncResult {
  pulled: number;
  pushed: number;
  conflicts: SyncConflict[];
  pendingAdditions: number;
  pendingRemovals: number;
  timestamp: string;
}

/** A single sync conflict requiring resolution. */
export interface SyncConflict {
  nodeId: string;
  field: keyof AdjacencyNode;
  localValue: unknown;
  remoteValue: unknown;
}

// ── Reference Data ───────────────────────────────────────────────────

/** Success probability by distance from core (Bain framework). */
export const RING_SUCCESS_RATES: Record<RelatednessRing, { label: string; rate: string }> = {
  0: { label: 'Core', rate: 'Baseline' },
  1: { label: '1-Step', rate: '~60%' },
  2: { label: '2-Step', rate: '~35%' },
  3: { label: '3-Step', rate: '~15%' },
  4: { label: 'Diversification', rate: '<5%' },
};

/**
 * Axis labels mapped to angular positions (degrees, clockwise from top).
 * 0° = 12 o'clock, 90° = 3 o'clock, 180° = 6 o'clock, 270° = 9 o'clock.
 * 8 non-Core axes at 45° spacing.
 */
export const AXIS_ANGLES: Record<AdjacencyAxis, number> = {
  'Core': -1,                              // Center, no angle
  'New Services': 0,                       // 12:00
  'New Markets & Customers': 45,           // 1:30
  'Forward Integration': 90,               // 3:00
  'New Technologies & Capabilities': 135,  // 4:30
  'New Channels': 180,                     // 6:00
  'New Geographies': 225,                  // 7:30
  'Backward Integration': 270,             // 9:00
  'New Products': 315,                     // 10:30
};

/** Descriptive labels for Bain dimension scores (1–10 scale). */
export const BAIN_SCORE_LABELS: Record<number, string> = {
  1: 'None',
  2: 'None',
  3: 'Minimal',
  4: 'Minimal',
  5: 'Partial',
  6: 'Partial',
  7: 'Strong',
  8: 'Strong',
  9: 'Full',
  10: 'Full',
};
